import api from './api';
import { CompanyStatusFilter } from './api/companyApi';

export interface TransportModeData {
  tot_emissions: number;
  tkm?: number;
  weight?: number;
  ei: number;
}

export type GHGOverviewSortField =
  | 'rank'
  | 'company_name'
  | 'total_tot_emissions'
  | 'total_ops_emissions'
  | 'total_ene_emissions'
  | 'total_ei'
  | 'total_tkm';

export interface GHGOverview {
  year: number;
  aggregated: {
    organizational_level: {
      tot_emissions: number;
      ops_emissions: number;
      ene_emissions: number;
      ei: number;
      tkm: number;
    };
    transport_chain_elements: {
      road: TransportModeData;
      air: TransportModeData;
      sea: TransportModeData;
      iww: TransportModeData;
      ferry: TransportModeData;
      rail: TransportModeData;
      hub: TransportModeData;
    };
  };
  sort?: {
    sortBy: GHGOverviewSortField;
    sortDir: 'asc' | 'desc';
  };
  companies: Array<{
    company_id: number;
    company_name: string;
    licence_type: string;
    rank: number;
    total_tot_emissions: number;
    total_ops_emissions: number;
    total_ene_emissions: number;
    total_ei: number;
    total_tkm: number;
    transport_modes: {
      road: TransportModeData;
      air: TransportModeData;
      sea: TransportModeData;
      iww: TransportModeData;
      ferry: TransportModeData;
      rail: TransportModeData;
      hub: TransportModeData;
    };
    partner_name: string | null;
    is_demo: boolean;
    active: boolean;
    license_start: string | null;
    license_end: string | null;
  }>;
  monthly: Array<{
    year: number;
    month: number;
    month_name: string;
    companies: Array<{
      company_id: number;
      company_name: string;
      licence_type: string;
      total_tot_emissions: number;
      total_ops_emissions: number;
      total_ene_emissions: number;
      total_ei: number;
      total_tkm: number;
      ytd_tot_emissions: number;
      ytd_ops_emissions: number;
      ytd_ene_emissions: number;
      ytd_ei: number;
      ytd_tkm: number;
    }>;
  }>;
}

export interface CompanyGHG {
  company: {
    id: number;
    name: string;
    licence_type: string;
    partner_name: string | null;
    is_demo: boolean;
    active: boolean;
    license_start: string | null;
    license_end: string | null;
  };
  year: number;
  availableYears: number[];
  yearly: {
    organizational_level: {
      tot_emissions: number;
      ops_emissions: number;
      ene_emissions: number;
      ei: number;
      tkm: number;
    };
    transport_chain_elements: {
      road: TransportModeData;
      air: TransportModeData;
      sea: TransportModeData;
      iww: TransportModeData;
      ferry: TransportModeData;
      rail: TransportModeData;
      hub: TransportModeData;
    };
  } | null;
  monthly: Array<{
    year: number;
    month: number;
    month_name: string;
    total_tot_emissions: number;
    total_ops_emissions: number;
    total_ene_emissions: number;
    total_ei: number;
    total_tkm: number;
    ytd_tot_emissions: number;
    ytd_ops_emissions: number;
    ytd_ene_emissions: number;
    ytd_ei: number;
    ytd_tkm: number;
  }>;
  daily: Array<{
    day: string;
    year: number;
    month: number;
    total_tot_emissions: number;
    total_ops_emissions: number;
    total_ene_emissions: number;
    total_ei: number;
    total_tkm: number;
  }>;
}

/**
 * Get GHG reporting overview for all companies (Master Admin only)
 */
export const getGHGOverview = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  sortBy?: GHGOverviewSortField;
  sortDir?: 'asc' | 'desc';
  statusFilter?: CompanyStatusFilter;
}): Promise<GHGOverview> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;
  if (options?.sortBy) params.sortBy = options.sortBy;
  if (options?.sortDir) params.sortDir = options.sortDir;
  if (options?.statusFilter) params.statusFilter = options.statusFilter;
  const response = await api.get('/ghg-reporting/overview', { params });
  return response.data.data;
};

/**
 * Get GHG reporting for a specific company
 */
export const getCompanyGHG = async (
  companyId: number,
  options?: {
    year?: number;
    fromDate?: string;
    toDate?: string;
  }
): Promise<CompanyGHG> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;
  const response = await api.get(`/ghg-reporting/company/${companyId}`, { params });
  return response.data.data;
};

/**
 * Get list of available years with GHG reporting data
 */
export const getGHGAvailableYears = async (): Promise<number[]> => {
  const response = await api.get('/ghg-reporting/years');
  return response.data.data.years;
};

/**
 * Shared helper: export GHG overview to a file (Excel or PDF).
 */
const exportGHGToFile = async (
  endpoint: string,
  blobType: string,
  fileExtension: string,
  options?: {
    year?: number;
    fromDate?: string;
    toDate?: string;
    companyId?: number;
    statusFilter?: CompanyStatusFilter;
  }
): Promise<string | null> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;
  if (options?.companyId) params.companyId = options.companyId;
  if (options?.statusFilter) params.statusFilter = options.statusFilter;

  const response = await api.get(endpoint, {
    params,
    responseType: 'blob',
  });

  const rawContentType = response.headers['content-type'];
  const contentType = typeof rawContentType === 'string' ? rawContentType : '';

  // Check if response is JSON (download URL) or blob (direct download)
  if (contentType.includes('application/json')) {
    const text = await response.data.text();
    const json = JSON.parse(text);
    return json.data?.downloadUrl || null;
  }

  // Direct blob download
  const blob = new Blob([response.data], { type: blobType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const contentDisposition = String(response.headers['content-disposition'] ?? '');
  const fileName = contentDisposition.split('filename=')[1]?.replaceAll('"', '')
    || `ghg-report-${options?.year || new Date().getFullYear()}.${fileExtension}`;
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return null;
};

/**
 * Export GHG overview to Excel file
 */
export const exportGHGToExcel = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  companyId?: number;
  statusFilter?: CompanyStatusFilter;
}): Promise<string | null> =>
  exportGHGToFile(
    '/ghg-reporting/export/excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xlsx',
    options
  );

/**
 * Export GHG overview to PDF file
 */
export const exportGHGToPdf = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  companyId?: number;
  statusFilter?: CompanyStatusFilter;
}): Promise<string | null> =>
  exportGHGToFile('/ghg-reporting/export/pdf', 'application/pdf', 'pdf', options);
